import type { TechlineStatusCode } from '@packages/clients';

import type { TechlineAssetEntity } from '../entities';
import type { AssetUpdateFields } from './types';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { buildAssetReplacementCommand, mapCaseStatusToUploadStatus, uploadAssetToPresignedUrl } from '../helpers';

/** Parameters for updating an existing asset on a Techline case. */
export type UpdateAssetParams = {
    /** The ID of the asset to update. */
    assetId: string;

    /** The case this asset belongs to. */
    caseNumber: string;

    /** The current case status code. */
    caseStatusCode: string;

    /** The original asset entity (needed for replacement uploads). */
    originalAsset: TechlineAssetEntity;

    /** The update to apply. */
    update: AssetUpdateFields;
};

/** Fields that can be changed when replacing or updating an asset blob. */
type AssetBlobUpdate = {
    /** The new file to upload. */
    blob?: Blob;

    /** The new display name for the asset. */
    name?: string;

    /** The new note for the asset. */
    note?: string;
};

/** Fields that can be changed in a metadata-only asset update. */
type AssetMetadataUpdate = {
    /** The new display name for the asset. */
    name?: string;

    /** The new note for the asset. */
    note?: string;
};

/**
 * Update an existing techline asset. Handles two distinct paths:
 * blob replacement (re-upload with a new file) and metadata-only
 * updates (name/note changes without re-uploading).
 */
export class UpdateAsset extends StsBackendOperation {
    /**
     * Update an asset's blob or metadata.
     *
     * @param params - the asset ID, case context, original asset, and update data
     * @throws {Error} when a blob replacement does not return an upload URL
     * @returns the new asset ID when a blob was replaced, or undefined for metadata-only updates
     */
    async exec(params: UpdateAssetParams): Promise<string | undefined> {
        const { assetId, caseNumber, caseStatusCode, originalAsset, update } = params;
        const uploadStatus = mapCaseStatusToUploadStatus(caseStatusCode);

        if (update.blob) {
            return this.replaceBlob(originalAsset, update, caseNumber, uploadStatus);
        } else if (update.name || update.note) {
            await this.updateMetadata(assetId, update);
        }

        return undefined;
    }

    /**
     * Replace the asset's file by creating a new asset that supersedes the original.
     *
     * @param originalAsset - the asset entity being replaced
     * @param update - the new blob and optional name/note
     * @param caseNumber - the case this asset belongs to
     * @param uploadStatus - the status to send with the request
     * @throws {Error} when the API does not return an upload URL
     * @returns the new asset ID that replaced the original
     */
    private async replaceBlob(
        originalAsset: TechlineAssetEntity,
        update: AssetBlobUpdate,
        caseNumber: string,
        uploadStatus: TechlineStatusCode
    ): Promise<string> {
        const command = buildAssetReplacementCommand({
            originalAsset,
            update,
            caseNumber,
            uploadStatus,
        });

        // Create the replacement asset record and extract the first result
        const response = await this.client.postAssets({ assetsBody: command });
        const created = response.success ? response.data.results?.assets?.[0] : undefined;

        if (!created?.presignedUpload) {
            throw new Error('Upload URL not returned');
        }

        // Upload the new file to the presigned URL; update.blob is guaranteed non-null
        // because replaceBlob is only called from exec when update.blob is truthy.
        if (update.blob) {
            await uploadAssetToPresignedUrl({
                uploadUrl: created.presignedUpload.url,
                headers: created.presignedUpload.httpHeaders,
                body: update.blob,
            });
        }

        return created.asset.assetId;
    }

    /**
     * Update only the asset's metadata (name and/or note) without re-uploading.
     *
     * @param assetId - the asset to update
     * @param update - the new name and/or note
     */
    private async updateMetadata(assetId: string, update: AssetMetadataUpdate): Promise<void> {
        await this.client.updateAssets({
            assetsBody: {
                assets: [{ assetId, name: update.name, note: update.note }],
            },
        });
    }
}
